import axios, { AxiosInstance } from 'axios';
import { authService } from '../auth/authService';
import { emitApiUiEvent } from './apiEvents';

// Base URL: use env override if provided, otherwise use relative URLs (same origin)
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
});

// Track consecutive auth failures (refresh token invalid/expired) — not network errors
let refreshFailureCount = 0;
const MAX_REFRESH_FAILURES = 4; // Only redirect after repeated auth failures; transient errors don't log doctor out

// Track ongoing refresh to prevent multiple concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

const TRUE_AUTH_FAILURE_CODES = new Set([
  'refresh_token_expired',
  'refresh_token_revoked',
  'user_disabled',
  'token_invalid',
]);

const getErrorCode = (err: any): string | undefined => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.error_code) return String(detail.error_code);
  if (err?.response?.data?.error_code) return String(err.response.data.error_code);
  return undefined;
};

// Attach auth headers (JWT + CSRF) for every request as per unified auth strategy
apiClient.interceptors.request.use((config) => {
  const token = authService.getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // For state-changing requests, include CSRF token (required for web apps)
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = authService.getCsrfToken();
    
    
    if (!csrfToken) {
      // CSRF token is required for state-changing requests
      // eslint-disable-next-line no-console
      console.warn('CSRF token missing for state-changing request. Will retry after refresh if backend returns CSRF error.');
      // Don't block the request - let the backend handle the error
      // The response interceptor will handle CSRF errors and retry with refreshed token
    } else {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  return config;
});

// Helper function to check if error is token-related
const isTokenError = (
  status: number | undefined,
  errorMessage: string,
  errorCode?: string
): boolean => {
  // Attempt refresh for auth failures on both 401 and 403.
  if (status !== 401 && status !== 403) {
    return false;
  }

  const stableAuthErrorCodes = [
    'refresh_token_expired',
    'refresh_token_revoked',
    'user_disabled',
    'token_invalid',
  ];
  if (errorCode && stableAuthErrorCodes.includes(errorCode)) {
    return true;
  }
  
  const tokenErrorKeywords = [
    'invalid or expired token',
    'could not validate credentials',
    'token expired',
    'token invalid',
    'authentication failed',
    'unauthorized',
    'invalid token',
    'expired token',
    'token has expired',
    'jwt expired',
    'jwt invalid',
    'credentials invalid',
  ];
  
  return tokenErrorKeywords.some(keyword => errorMessage.includes(keyword));
};

// Helper function to check if error is CSRF-related
const isCsrfError = (status: number | undefined, errorMessage: string): boolean => {
  if (status !== 403) {
    return false;
  }
  
  const csrfErrorKeywords = [
    'csrf',
    'invalid csrf token',
    'csrf token',
    'cross-site request',
  ];
  
  return csrfErrorKeywords.some(keyword => errorMessage.includes(keyword));
};

// Centralized token refresh: use refresh_token to get new access token. Only count auth failures (401/403 from refresh), not network errors.
const handleTokenRefresh = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
        throw new Error('Too many token refresh failures.');
      }

      const newToken = await authService.refreshAccessToken();
      refreshFailureCount = 0;
      return newToken;
    } catch (refreshError: any) {
      // Only count as auth failure when refresh endpoint returns 401/403 (invalid/expired refresh token)
      const status = refreshError?.response?.status;
      const code = getErrorCode(refreshError);
      if (code && TRUE_AUTH_FAILURE_CODES.has(code)) {
        throw refreshError;
      }
      if (status === 401 || status === 403) {
        refreshFailureCount++;
        if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
          throw new Error('Session could not be validated.');
        }
      }
      // Network/5xx/other: do not increment; next request can try again
      throw refreshError;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const isIdempotent = (method: string | undefined) => {
  const m = (method || 'get').toUpperCase();
  return m === 'GET' || m === 'HEAD' || m === 'OPTIONS';
};

// Automatically refresh access token on invalid/expired token and retry once
apiClient.interceptors.response.use(
  (response) => {
    // Reset refresh failure count on successful response
    refreshFailureCount = 0;
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const errorData = error?.response?.data;
    
    // Extract error message from various possible locations
    let message = '';
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail.map((d: any) => d?.msg || JSON.stringify(d)).join(', ');
    } else if (detail && typeof detail === 'object' && (detail as any).message) {
      message = String((detail as any).message);
    } else if (detail) {
      message = JSON.stringify(detail);
    } else if (typeof errorData === 'string') {
      message = errorData;
    } else if (errorData?.message) {
      message = errorData.message;
    } else if (errorData?.error) {
      message = errorData.error;
    } else if (error?.message) {
      message = error.message;
    }
    
    const errorMessage = (typeof message === 'string' ? message : String(message)).toLowerCase();
    const errorCode: string | undefined =
      (detail && typeof detail === 'object' ? (detail as any).error_code : undefined) ||
      (errorData && typeof errorData === 'object' ? (errorData as any).error_code : undefined) ||
      (errorData && typeof errorData === 'object' ? (errorData as any).code : undefined);

    const originalRequest = error.config as any;

    // "Service unavailable" message is reserved only for HTTP 503.
    if (status === 503) {
      emitApiUiEvent({
        type: 'service_unavailable',
        message: 'Service unavailable. Please try again.',
      });
      return Promise.reject(error);
    }

    // Handle network/edge gateway errors (no response / 502 / 504)
    if (!error.response || [502, 504].includes(status)) {
      const hasAuthContext = !!(
        originalRequest?.headers?.Authorization ||
        authService.getToken()
      );
      const looksLikeAuthFailure =
        /\b401\b|\b403\b|unauthoriz|forbidden|not\s+authenticated|credentials/i.test(errorMessage || '') ||
        (hasAuthContext && typeof navigator !== 'undefined' && navigator.onLine);

      // Retry once for idempotent requests. Do NOT logout on service/network errors.
      if (originalRequest && !originalRequest._serviceRetry && isIdempotent(originalRequest.method)) {
        originalRequest._serviceRetry = true;
        try {
          // Short delay before retry.
          await new Promise((r) => setTimeout(r, 750));
          // Ensure latest token is attached
          const token = authService.getToken();
          if (token) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
          }
          return await apiClient(originalRequest);
        } catch (retryErr) {
          emitApiUiEvent({
            type: 'session_issue',
            message: looksLikeAuthFailure
              ? 'Request was rejected (authorization/session issue). Please log in again if this keeps happening.'
              : 'Unable to reach the server right now. Please try again.',
          });
          return Promise.reject(retryErr);
        }
      }

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[auth] network/service error (no logout)', {
          url: originalRequest?.url,
          status,
        });
      }
      emitApiUiEvent({
        type: 'session_issue',
        message: looksLikeAuthFailure
          ? 'Request was rejected (authorization/session issue). Please log in again if this keeps happening.'
          : 'Unable to reach the server right now. Please try again.',
      });
      return Promise.reject(error);
    }

    // Skip retry if request was already retried
    if (originalRequest?._retry || originalRequest?._csrfRetry || originalRequest?._authRetry) {
      return Promise.reject(error);
    }

    const redirectToLogin = () => {
      try {
        authService.clearTokens();
      } catch {
        // ignore
      }
      try {
        window.location.replace('/login?error=session_expired');
      } catch {
        // ignore
      }
    };

    const notifySessionIssue = (code?: string, err?: any) => {
      const refreshErrorMessage = String(err?.message || '').toLowerCase();
      const hasRefreshToken = !!authService.getRefreshToken();
      const mustRelogin =
        (!hasRefreshToken && (status === 401 || status === 403)) ||
        (code ? TRUE_AUTH_FAILURE_CODES.has(code) : false) ||
        refreshErrorMessage.includes('too many token refresh failures') ||
        refreshErrorMessage.includes('missing refresh token') ||
        refreshErrorMessage.includes('session could not be validated');

      if (mustRelogin) {
        redirectToLogin();
        return;
      }
      // Use message that matches the actual error: 401/403 are auth/permission, not network down
      const message =
        status === 401
          ? 'Your session has expired. Please log in again.'
          : status === 403
            ? "You don't have permission to access this resource or it may no longer be available."
            : 'Network is down or service is not reachable. Please try again.';
      emitApiUiEvent({
        type: 'session_issue',
        message,
      });
    };

    // ACL/permission 403 should not trigger refresh.
    if (status === 403 && errorCode === 'insufficient_scope') {
      notifySessionIssue(errorCode, error);
      return Promise.reject(error);
    }

    // Handle CSRF token errors (403 with CSRF-related message)
    if (isCsrfError(status, errorMessage)) {
      originalRequest._csrfRetry = true;
      
      try {
        // Refresh token (which also refreshes CSRF token)
        await handleTokenRefresh();
        const newCsrfToken = authService.getCsrfToken();
        
        if (newCsrfToken) {
          // Update CSRF token header and retry
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-CSRF-Token'] = newCsrfToken;
          // Also update auth token in case it changed
          const newToken = authService.getToken();
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          }
          return apiClient(originalRequest);
        } else {
          console.warn('Failed to get CSRF token after refresh');
          notifySessionIssue();
          return Promise.reject(error);
        }
      } catch (csrfRefreshError) {
        console.error('Failed to refresh CSRF token:', csrfRefreshError);
        notifySessionIssue(getErrorCode(csrfRefreshError), csrfRefreshError);
        return Promise.reject(error);
      }
    }

    // Handle token expiration errors (401/403 with token-related message)
    if (isTokenError(status, errorMessage, errorCode)) {
      originalRequest._retry = true;

      try {
        const newToken = await handleTokenRefresh();
        const newCsrfToken = authService.getCsrfToken();

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        if (newCsrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes((originalRequest.method || 'get').toUpperCase())) {
          originalRequest.headers['X-CSRF-Token'] = newCsrfToken;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        notifySessionIssue(getErrorCode(refreshError) || errorCode, refreshError);
        return Promise.reject(refreshError);
      }
    }

    // Permission failures (403) should not trigger token refresh.
    if (status === 403) {
      notifySessionIssue(errorCode, error);
      return Promise.reject(error);
    }

    // Fallback: 401 only — try refresh when we have a refresh token (redirect only when refresh is missing/fails)
    if (status === 401) {
      // First, retry once with the existing token (covers transient gateway/auth propagation issues).
      if (originalRequest && !originalRequest._authRetry) {
        originalRequest._authRetry = true;
        const token = authService.getToken();
        if (token) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
        }
        try {
          return await apiClient(originalRequest);
        } catch (e) {
          // fall through to refresh (if available) or notify
        }
      }

      const hasRefreshToken = !!authService.getRefreshToken();
      if (hasRefreshToken) {
        originalRequest._retry = true;
        try {
          const newToken = await handleTokenRefresh();
          const newCsrfToken = authService.getCsrfToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          if (newCsrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes((originalRequest.method || 'get').toUpperCase())) {
            originalRequest.headers['X-CSRF-Token'] = newCsrfToken;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          notifySessionIssue(getErrorCode(refreshError) || errorCode, refreshError);
          return Promise.reject(refreshError);
        }
      }
      notifySessionIssue(errorCode, new Error('Missing refresh token'));
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export { apiClient };


